/**
 * TransactionCard (PHASE1 §7.2). Renders the inbox item currently awaiting a
 * decision: counterparty, signed amount, booking date, purpose, tags, the
 * account it belongs to, a PSP deep link (Amazon/PayPal) when detected, and an
 * inline Notiz editor (PATCH via `useSaveInboxNote` — cache-patching, never an
 * inbox refetch). Money always flows through `AmountText`/`format.money`.
 */
import { useState } from 'react';
import { CalendarDays, ExternalLink, Landmark, StickyNote } from 'lucide-react';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Textarea } from '../../components/ui/Textarea.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { formatDate } from '../../lib/format.ts';
import { SOURCE } from '../../lib/labels.ts';
import { orderLink } from '../../lib/orderLinks.ts';
import { useSaveInboxNote } from '../../lib/hooks/useInbox.ts';
import type { InboxItem } from '../../lib/types.ts';

/** Inline Notiz: collapsed display (or "Notiz hinzufügen"), expands to a
 * textarea. Keyed by `item.id` at the call site so drafts never leak onto the
 * next card when the queue advances. */
function NoteEditor({ item }: { item: InboxItem }) {
  const { toast } = useToast();
  const save = useSaveInboxNote();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.notes ?? '');

  function startEdit() {
    setDraft(item.notes ?? '');
    setEditing(true);
  }

  function saveNote() {
    const value = draft.trim();
    save.mutate(
      { id: item.id, notes: value === '' ? null : value },
      {
        onSuccess: () => setEditing(false),
        onError: (error) =>
          toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
            variant: 'error',
          }),
      },
    );
  }

  if (!editing) {
    if (item.notes) {
      return (
        <div className="mt-2 flex items-start gap-1.5 text-sm text-slate-600 dark:text-slate-300">
          <StickyNote
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400"
            aria-hidden
          />
          <span className="min-w-0 break-words italic">{item.notes}</span>
          <button
            type="button"
            onClick={startEdit}
            className="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
          >
            Bearbeiten
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={startEdit}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md py-0.5 text-xs font-medium text-slate-400 transition-colors duration-150 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
      >
        <StickyNote className="h-3.5 w-3.5" aria-hidden />
        Notiz hinzufügen
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <Textarea
        rows={2}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Was steckt hinter dieser Transaktion?"
        aria-label="Notiz"
        autoFocus
        disabled={save.isPending}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>
          Abbrechen
        </Button>
        <Button variant="secondary" size="sm" onClick={saveNote} disabled={save.isPending}>
          {save.isPending ? 'Speichert …' : 'Notiz speichern'}
        </Button>
      </div>
    </div>
  );
}

export interface TransactionCardProps {
  item: InboxItem;
}

export function TransactionCard({ item }: TransactionCardProps) {
  const payee = item.counterparty_name?.trim() || item.normalized_payee?.trim() || 'Unbekannt';
  // Enriched card-acceptor (e.g. "Dulfs Burger") outranks the masking PSP
  // ("Adyen N.V.") as the headline; the PSP stays visible as secondary text.
  const displayPayee = item.merchant_name || payee;
  const link = orderLink(item);

  return (
    <article className="py-4">
      <div className="flex items-center justify-between gap-3 text-xs text-slate-400 dark:text-slate-500">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Landmark className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{item.account_name}</span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden />
          {formatDate(item.booking_date)}
        </span>
      </div>

      <div className="mt-4 flex items-start justify-between gap-4">
        <h2 className="min-w-0 text-lg font-semibold text-slate-900 dark:text-slate-100">
          <span className="break-words">{displayPayee}</span>
          {item.merchant_name ? (
            <span className="ml-2 text-sm font-normal text-slate-400 dark:text-slate-500">
              über {payee}
            </span>
          ) : null}
        </h2>
        <AmountText value={item.amount} className="shrink-0 text-xl" showPlus />
      </div>

      {item.purpose ? (
        <p className="mt-2 whitespace-pre-line break-words text-sm text-slate-600 dark:text-slate-400">
          {item.purpose}
        </p>
      ) : null}

      {item.booking_text ? (
        <p className="mt-1 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {item.booking_text}
        </p>
      ) : null}

      <NoteEditor key={item.id} item={item} />

      {item.counterparty_iban ? (
        <p className="mt-2 font-mono text-xs text-slate-400 dark:text-slate-500">
          {item.counterparty_iban}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {SOURCE[item.source]}
        </span>
        {link ? (
          <a
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 transition-colors duration-150 hover:bg-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:hover:bg-sky-900"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            {link.label}
          </a>
        ) : null}
        {item.tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-950 dark:text-sky-300"
          >
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
}
